import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/tables/DataTable';
import ViewTicketModal from '../../components/features/tickets/ViewTicketModal';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import engineerService from '../../services/engineerService';
import { formatDateTime, parseBackendDate } from '../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS, TICKET_STATUSES } from '../../constants/statuses';

const MyTicketsView = ({ onStartWorkNavigate, defaultFilter = '' }) => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [activeFilter, setActiveFilter] = useState(defaultFilter);
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    const result = await engineerService.getMyTickets(user.id);
    if (result.success) {
      setTickets(result.data);
    }
    setLoading(false);
  };

  const handleStartWork = async (ticket) => {
    const result = await engineerService.startWork(ticket.id);
    if (result.success) {
      success('Work started on ticket!');
      await loadTickets();
      
      if (typeof onStartWorkNavigate === 'function') {
        onStartWorkNavigate(result.data || ticket);
      }
    } else {
      error(result.error || 'Failed to start work');
    }
  };

  const handleCompleteTicket = (ticket) => {
    if (typeof onStartWorkNavigate === 'function') {
      onStartWorkNavigate(ticket);
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowViewModal(true);
  };

  const activeTickets = tickets.filter(t => 
    [TICKET_STATUSES.ASSIGNED, TICKET_STATUSES.REASSIGNED, TICKET_STATUSES.IN_PROGRESS, TICKET_STATUSES.PENDING, TICKET_STATUSES.REOPENED].includes(t.status)
  );

  const completedTickets = tickets.filter(t => 
    t.status === TICKET_STATUSES.CLOSED
  );

  const getFilteredTickets = () => {
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    const nowUTC = new Date();
    const nowIST = new Date(nowUTC.getTime() + IST_OFFSET_MS);

    // Get today's date in IST
    const istYear = nowIST.getUTCFullYear();
    const istMonth = nowIST.getUTCMonth();
    const istDate = nowIST.getUTCDate();

    // todayStart and todayEnd expressed as UTC timestamps
    const todayStart = new Date(Date.UTC(istYear, istMonth, istDate, 0, 0, 0) - IST_OFFSET_MS);
    const todayEnd   = new Date(Date.UTC(istYear, istMonth, istDate, 23, 59, 59, 999) - IST_OFFSET_MS);

    if (activeFilter === 'ASSIGNED_TODAY') {
      return tickets.filter(t => {
        const assignedFor = parseBackendDate(t.assigned_for);
        return assignedFor && assignedFor >= todayStart && assignedFor <= todayEnd;
      });
    }
    if (activeFilter === 'IN_PROGRESS') {
      return tickets.filter(t => t.status === TICKET_STATUSES.WORK_IN_PROGRESS);
    }
    if (activeFilter === 'COMPLETED_TODAY') {
        return tickets.filter(t => {
          if (t.status !== TICKET_STATUSES.CLOSED) return false;
          const closedAt = parseBackendDate(t.closed_at);
          return closedAt && closedAt >= todayStart && closedAt <= todayEnd;
        });
    }
    return null;
  };

  const filterOverride = getFilteredTickets();

  const sectionStyle = {
    marginBottom: '32px'
  };

  const sectionTitleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginBottom: '20px'
  };

  // Active Tickets columns
  const activeColumns = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (value) => (
        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
          #{value}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {row.system_type}
          </div>
        </div>
      )
    },
    {
      key: 'site_name',
      label: 'Site',
      sortable: true
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${PRIORITY_COLORS[value] || '#6b7280'}20`,
          color: PRIORITY_COLORS[value] || '#6b7280'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${STATUS_COLORS[value] || '#6b7280'}20`,
          color: STATUS_COLORS[value] || '#6b7280'
        }}>
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value) => formatDateTime(value)
    },
    {
      key: 'metrics',
      label: 'SLA',
      render: (value) => (
        value?.is_overdue ? (
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            background: '#ef444420',
            color: '#ef4444'
          }}>
            At Risk
          </span>
        ) : (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>On Track</span>
        )
      )
    }
  ];

  // Completed Tickets columns
  const completedColumns = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (value) => (
        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
          #{value}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true
    },
    {
      key: 'site_name',
      label: 'Site',
      sortable: true
    },
    {
      key: 'system_type',
      label: 'Type',
      sortable: true
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${PRIORITY_COLORS[value] || '#6b7280'}20`,
          color: PRIORITY_COLORS[value] || '#6b7280'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${STATUS_COLORS[value] || '#6b7280'}20`,
          color: STATUS_COLORS[value] || '#6b7280'
        }}>
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'closed_at',
      label: 'Completed',
      sortable: true,
      render: (value) => value ? formatDateTime(value) : '-'
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading tickets...
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Active Tickets Table */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Active Tickets - Action Required</h3>
        {(filterOverride !== null && activeFilter !== 'COMPLETED_TODAY' ? filterOverride : activeTickets).length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px', 
            color: 'var(--text-secondary)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px'
          }}>
            No active tickets assigned
          </div>
        ) : (
          <DataTable
            data={filterOverride !== null  && activeFilter !== 'COMPLETED_TODAY' ? filterOverride : activeTickets}
            columns={activeColumns}
            onRowClick={handleTicketClick}
            actions={[
              { 
                label: 'View', 
                onClick: handleTicketClick 
              },
              { 
                label: 'Start Work', 
                onClick: handleStartWork,
                condition: (ticket) => ticket.status === TICKET_STATUSES.ASSIGNED || ticket.status === TICKET_STATUSES.REASSIGNED || ticket.status === TICKET_STATUSES.REOPENED
              },
              { 
                label: 'Complete', 
                onClick: handleCompleteTicket,
                condition: (ticket) => ticket.status === TICKET_STATUSES.WORK_IN_PROGRESS
              }
            ]}
          />
        )}
      </div>

      {/* Completed Tickets Table */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Completed Tickets</h3>
        {(filterOverride !== null && activeFilter === 'COMPLETED_TODAY' ? filterOverride : completedTickets).length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px', 
            color: 'var(--text-secondary)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px'
          }}>
            No completed tickets yet
          </div>
        ) : (
          <DataTable
            data={filterOverride !== null && activeFilter === 'COMPLETED_TODAY' ? filterOverride : completedTickets}
            columns={completedColumns}
            onRowClick={handleTicketClick}
            actions={[
              { label: 'View', onClick: handleTicketClick }
            ]}
          />
        )}
      </div>

      {/* View Ticket Modal */}
      {showViewModal && selectedTicket && (
        <ViewTicketModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedTicket(null);
          }}
          ticket={selectedTicket}
        />
      )}
    </div>
  );
};

export default MyTicketsView;